import { useCallback, useMemo, useState } from "react";
import { GridSortModel } from "@mui/x-data-grid";
import {
  AggregateScoreOrderField,
  AggregateScoreType,
  OrderDirection,
  useGetAggregateScoresQuery,
} from "~/operations";
import { usePolicyOutletContext } from "../hooks/usePolicyOutletContext";
import { useDataGridPagination } from "~/hooks";
import { ChecksTable } from "~/pages/platform/policy/components/ChecksTable";
import { PolicyCheckNode } from "~/pages/platform/policy/types";
import { Box } from "@mui/material";
import { usePolicyChecksFilterContext } from "~/pages/platform/policy/tabs/hooks/usePolicyChecksFilterContext";
import { PolicyAssignScope } from "../utils";

// Map DataGrid column fields to GraphQL sort fields
const SORT_FIELD_MAP: Record<string, AggregateScoreOrderField> = {
  title: AggregateScoreOrderField.Title,
  riskValue: AggregateScoreOrderField.RiskValue,
  blastRadius: AggregateScoreOrderField.BlastRadius,
  completion: AggregateScoreOrderField.Completion,
};

const DEFAULT_SORT_MODEL: GridSortModel = [{ field: "riskValue", sort: "asc" }];

export interface PolicyChecksTabProps {
  assignedScope: PolicyAssignScope;
  basePath?: string;
}

export function PolicyChecksTab({
  assignedScope,
  basePath,
}: PolicyChecksTabProps) {
  const { policy, policyId } = usePolicyOutletContext();

  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);

  // Convert DataGrid sort model to GraphQL orderBy
  const orderBy = useMemo(() => {
    if (sortModel.length === 0) {
      return {
        field: AggregateScoreOrderField.RiskValue,
        direction: OrderDirection.Asc,
      };
    }
    const { field, sort } = sortModel[0];
    return {
      field: SORT_FIELD_MAP[field] || AggregateScoreOrderField.RiskValue,
      direction: sort === "desc" ? OrderDirection.Desc : OrderDirection.Asc,
    };
  }, [sortModel]);

  const { filterVariables, filterForm } = usePolicyChecksFilterContext();

  // Initialize pagination with undefined - will be updated after query
  const pagination = useDataGridPagination({
    prefix: "checks",
    defaultPageSize: 25,
    pageInfo: undefined,
    totalCount: undefined,
    resetDeps: [filterVariables, orderBy.field, orderBy.direction],
  });

  const { data, loading } = useGetAggregateScoresQuery({
    variables: {
      entityMrn: assignedScope.mrn,
      first: pagination.first,
      after: pagination.after,
      orderBy,
      filter: {
        scoreType: AggregateScoreType.Check,
        policyMrn: policy.mrn,
        ...filterVariables,
      },
      skipCases: true,
    },
    fetchPolicy: "cache-and-network",
  });

  const aggregateScores =
    data?.aggregateScores?.__typename === "AggregateScoresConnection"
      ? data.aggregateScores
      : undefined;

  // Get pagination with actual data - this will update rowCount properly
  const paginationWithData = useDataGridPagination({
    prefix: "checks",
    defaultPageSize: 25,
    pageInfo: aggregateScores?.pageInfo,
    totalCount: aggregateScores?.totalCount,
    resetDeps: [filterVariables, orderBy.field, orderBy.direction],
  });

  const edges = aggregateScores?.edges || [];

  const rows: PolicyCheckNode[] = edges
    .map((edge) => edge?.node)
    .filter(
      (node): node is PolicyCheckNode => node !== null && node !== undefined,
    );

  const onSortModelChange = useCallback((newModel: GridSortModel) => {
    setSortModel(newModel.length > 0 ? newModel : DEFAULT_SORT_MODEL);
  }, []);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      {filterForm}
      <ChecksTable
        rows={rows}
        loading={loading}
        policyId={policyId}
        paginationModel={paginationWithData.paginationModel}
        onPaginationModelChange={paginationWithData.onPaginationModelChange}
        rowCount={paginationWithData.rowCount}
        sortModel={sortModel}
        onSortModelChange={onSortModelChange}
        basePath={basePath}
        scopeParams={assignedScope.params}
      />
    </Box>
  );
}
